import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import QRCode from 'qrcode'
import { ApiClientError } from '@/lib/api/client'
import {
  createMascotEnergyPurchase,
  fetchMascotEnergyCatalog,
  fetchMascotEnergyPurchase,
  type MascotEnergyCatalog,
  type MascotEnergyPurchase,
} from './mascot-energy-api'
import styles from './MascotGame.module.css'
import paymentStyles from './MascotCoinShop.module.css'

const price = (cents: number) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const errorMessage = (error: unknown) =>
  error instanceof ApiClientError
    ? Object.values(error.errors ?? {}).flat()[0] || error.message
    : 'Não foi possível consultar a compra. Tente novamente.'

type Props = {
  campaignId: number
  energy: number
  disabled: boolean
  onPaid: () => Promise<void>
}

export function MascotEnergyPill(props: Props) {
  return <EnergyPillCheckout key={props.campaignId} {...props} />
}

function EnergyPillCheckout({ campaignId, energy, disabled, onPaid }: Props) {
  const [catalog, setCatalog] = useState<MascotEnergyCatalog | null>(null)
  const [purchase, setPurchase] = useState<MascotEnergyPurchase | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [qr, setQr] = useState('')
  const [copied, setCopied] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const active = useRef(false)
  const creating = useRef(false)
  const reading = useRef(false)
  const manualController = useRef<AbortController | null>(null)
  const requestId = useRef<string | null>(null)
  const lastPurchase = useRef<MascotEnergyPurchase | null>(null)
  const notified = useRef<string | null>(null)
  const onPaidRef = useRef(onPaid)
  useEffect(() => {
    onPaidRef.current = onPaid
  }, [onPaid])

  const accept = useCallback((next: MascotEnergyPurchase | null) => {
    if (
      next?.id === lastPurchase.current?.id &&
      lastPurchase.current?.status === 'paid' &&
      next?.status !== 'paid'
    )
      return
    lastPurchase.current = next
    setPurchase(next)
    if (next?.status === 'paid' && next.fulfilled_at && notified.current !== next.id) {
      notified.current = next.id
      void onPaidRef.current().catch(() => {
        if (active.current)
          setError('Pagamento confirmado. Atualize o jogo para ver a energia recuperada.')
      })
    }
  }, [])

  useEffect(() => {
    active.current = true
    const controller = new AbortController()
    setLoading(true)
    setError('')
    void fetchMascotEnergyCatalog(campaignId, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          setCatalog(data)
          accept(data.purchase)
        }
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setError(errorMessage(cause))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => {
      active.current = false
      controller.abort()
      manualController.current?.abort()
    }
  }, [campaignId, attempt, accept])

  useEffect(() => {
    setQr('')
    setCopied(false)
    if (!purchase?.pix_key || purchase.status !== 'pending') return
    let cancelled = false
    void QRCode.toDataURL(purchase.pix_key, { width: 240, margin: 2 })
      .then((url) => {
        if (!cancelled) setQr(url)
      })
      .catch(() => {
        /* Copy-and-paste remains available. */
      })
    return () => {
      cancelled = true
    }
  }, [purchase?.pix_key, purchase?.status])

  const purchaseId = purchase?.id
  const purchaseStatus = purchase?.status
  useEffect(() => {
    if (
      !purchaseId ||
      !purchaseStatus ||
      !['creating', 'creation_uncertain', 'pending'].includes(purchaseStatus)
    )
      return
    const controller = new AbortController()
    const started = Date.now()
    let timer: number | undefined
    const id = purchaseId
    async function poll() {
      if (controller.signal.aborted || Date.now() - started >= 300_000) return
      if (!document.hidden && !reading.current && !creating.current) {
        reading.current = true
        try {
          const next = await fetchMascotEnergyPurchase(id, controller.signal)
          if (controller.signal.aborted) return
          accept(next)
          if (['paid', 'expired'].includes(next.status)) return
        } catch {
          /* Keep the original PIX visible and offer manual lookup. */
        } finally {
          reading.current = false
        }
      }
      if (!controller.signal.aborted) timer = window.setTimeout(() => void poll(), 5000)
    }
    timer = window.setTimeout(() => void poll(), 5000)
    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [purchaseId, purchaseStatus, accept])

  async function buy() {
    if (
      creating.current ||
      reading.current ||
      disabled ||
      !catalog?.sales_open ||
      energy >= 100
    )
      return
    creating.current = true
    setBusy(true)
    setError('')
    requestId.current ??= crypto.randomUUID()
    try {
      const next = await createMascotEnergyPurchase(campaignId, requestId.current)
      if (active.current) {
        accept(next)
        requestId.current = null
      }
    } catch (cause) {
      if (active.current) {
        setError(errorMessage(cause))
        if (cause instanceof ApiClientError && cause.status >= 400 && cause.status < 500)
          requestId.current = null
      }
    } finally {
      creating.current = false
      if (active.current) setBusy(false)
    }
  }

  async function refresh() {
    if (!purchase || reading.current || creating.current) return
    reading.current = true
    setBusy(true)
    setError('')
    const controller = new AbortController()
    manualController.current = controller
    try {
      const next = await fetchMascotEnergyPurchase(purchase.id, controller.signal)
      if (active.current) accept(next)
    } catch (cause) {
      if (active.current) setError(errorMessage(cause))
    } finally {
      reading.current = false
      if (active.current) setBusy(false)
    }
  }

  const unresolved = purchase && purchase.status !== 'paid'
  return (
    <section
      className={`${paymentStyles.root} ${styles.energyPill}`}
      aria-label="Pílula de energia"
    >
      <div className={paymentStyles.intro}>
        <h3>
          <Icon icon="mdi:pill" /> Pílula de energia
        </h3>
        <p>
          Por {price(catalog?.amount_cents ?? 1000)}, a energia do seu mascote será
          preenchida até 100% após a confirmação do Pix.
        </p>
        <p>O sono continua disponível gratuitamente.</p>
      </div>
      {loading && <p role="status">Consultando a pílula e sua compra…</p>}
      {error && (
        <p role="alert" className={paymentStyles.notice}>
          {error}
        </p>
      )}
      {!loading && !catalog && (
        <button
          type="button"
          className={styles.secondaryButton}
          onClick={() => setAttempt((value) => value + 1)}
        >
          Tentar novamente
        </button>
      )}
      {catalog && !loading && (
        <>
          {!catalog.sales_open && (
            <p className={paymentStyles.notice}>
              A compra de energia está indisponível nesta campanha. Você pode consultar um
              Pix já iniciado.
            </p>
          )}
          {!unresolved && (
            <button
              type="button"
              className={styles.primaryButton}
              disabled={busy || disabled || !catalog.sales_open || energy >= 100}
              onClick={() => void buy()}
            >
              <Icon icon="mdi:qrcode" />{' '}
              {busy
                ? 'Preparando Pix…'
                : energy >= 100
                  ? 'Energia já está em 100%'
                  : `Comprar pílula por ${price(catalog.amount_cents)}`}
            </button>
          )}
        </>
      )}
      {purchase && (
        <section
          className={paymentStyles.payment}
          aria-label="Pagamento da pílula de energia"
        >
          <h3>Pílula de energia · {price(purchase.amount_cents)}</h3>
          {purchase.campaign_id !== campaignId && (
            <p>
              Este Pix foi iniciado em outra campanha. Acompanhe esta compra antes de
              gerar outro pagamento.
            </p>
          )}
          {purchase.status === 'paid' ? (
            <>
              <p role="status" className={paymentStyles.success}>
                Pagamento confirmado! A energia do mascote foi preenchida até 100%.
              </p>
              <button
                type="button"
                className={styles.secondaryButton}
                disabled={disabled}
                onClick={() =>
                  void onPaidRef
                    .current()
                    .catch(() => setError('Atualize o jogo para ver a energia.'))
                }
              >
                Atualizar energia
              </button>
            </>
          ) : purchase.status === 'pending' ? (
            <>
              <p role="status">
                Aguardando pagamento. A energia será recuperada após a confirmação.
              </p>
              {qr && (
                <img
                  className={paymentStyles.qr}
                  src={qr}
                  alt="QR Code Pix da pílula de energia"
                  width={240}
                  height={240}
                />
              )}
              <label className={paymentStyles.codeLabel}>
                Pix copia e cola
                <textarea readOnly value={purchase.pix_key ?? ''} />
              </label>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => {
                  if (!navigator.clipboard) {
                    setError('Selecione e copie o código Pix acima.')
                    return
                  }
                  void navigator.clipboard
                    .writeText(purchase.pix_key ?? '')
                    .then(() => setCopied(true))
                    .catch(() => setError('Selecione e copie o código Pix acima.'))
                }}
              >
                {copied ? 'Código copiado!' : 'Copiar código Pix'}
              </button>
              {purchase.expires_at && (
                <small>
                  Prazo de exibição:{' '}
                  {new Date(purchase.expires_at).toLocaleString('pt-BR')}
                </small>
              )}
            </>
          ) : purchase.status === 'expired' ? (
            <p role="status">
              O prazo de exibição deste Pix terminou. Consulte a confirmação ou contate o
              suporte antes de fazer outro pagamento.
            </p>
          ) : purchase.status === 'creating' ? (
            <p role="status">Preparando sua cobrança…</p>
          ) : (
            <p role="status">
              A criação do Pix precisa de confirmação. Consulte este pedido; não repita o
              pagamento. Se persistir, informe o código abaixo ao suporte.
            </p>
          )}
          {purchase.status !== 'paid' && (
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={busy}
              onClick={() => void refresh()}
            >
              {busy ? 'Consultando…' : 'Consultar pagamento'}
            </button>
          )}
          <small className={paymentStyles.reference}>Pedido {purchase.id}</small>
        </section>
      )}
    </section>
  )
}
